import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link, useNavigate } from "react-router-dom";
import { ChevronDown, PlayCircle, AlertCircle, Lock } from "lucide-react";
import UpgradeBanner from "../../components/Billing/UpgradeBanner";
import Layout from "../../components/Layout/Layout";
import api from "../../services/api";
import ProgressBar from "./ProgressBar";
import StatusIcon from "./StatusIcon";
import { errMsg, classQuery } from "./courseUtils";
import "./courses.css";

const CourseSubject = () => {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const cls = params.get("class") || "";
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [openTerms, setOpenTerms] = useState({});
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    api
      .get(`/api/courses/${slug}`, { params: cls ? { class: cls } : {}, timeout: 60000 })
      .then((res) => {
        if (cancelled) return;
        setData(res.data);
        const terms = res.data.terms || [];
        const first = terms.find((t) => t.chapters_total > t.chapters_passed) || terms[0];
        setOpenTerms(first ? { [first.term]: true } : {});
      })
      .catch((e) => !cancelled && setError(errMsg(e, "We couldn't prepare this course right now.")))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [slug, cls, reloadKey]);

  const activeClass = data?.class || cls;
  const q = classQuery(activeClass);
  const next = data?.next_chapter;

  return (
    <Layout fullBleed>
      <div className="courses-page">
        <div className="cs-shell">
          <nav className="cs-crumbs" aria-label="Breadcrumb">
            <Link to={`/courses${q}`}>Courses</Link>
            <span aria-hidden="true">›</span>
            <span aria-current="page">{data?.subject || "Subject"}</span>
          </nav>

          {loading && (
            <div aria-busy="true">
              <p className="cs-loading-text" role="status">
                Preparing your course… the first time can take a few seconds
              </p>
              <div className="cs-skel cs-skel-header" />
              {[0, 1, 2].map((i) => (
                <div key={i} className="cs-card cs-skel-card">
                  <div className="cs-skel cs-skel-line" />
                  <div className="cs-skel cs-skel-line short" />
                </div>
              ))}
            </div>
          )}

          {!loading && error && (
            <div className="cs-alert" role="alert">
              <AlertCircle size={18} />
              <span>{error}</span>
              <button type="button" className="eg-btn eg-btn-outline" onClick={() => setReloadKey((k) => k + 1)}>
                Retry
              </button>
            </div>
          )}

          {!loading && data && (
            <>
              <UpgradeBanner billing={data.billing} />
              <header className="cs-subject-head">
                <div className="cs-subject-head-main">
                  <h1 className="cs-title">{data.subject}</h1>
                  <p className="cs-subtitle">{activeClass}</p>
                  <div className="cs-overall">
                    <ProgressBar value={data.totals?.percent ?? 0} label="Overall progress" />
                    <span className="cs-muted-sm">
                      {data.totals?.chapters_passed ?? 0} of {data.totals?.chapters_total ?? 0} chapters · {data.totals?.percent ?? 0}%
                    </span>
                  </div>
                </div>
                {next ? (
                  <button
                    type="button"
                    className="eg-btn eg-btn-accent cs-cta"
                    onClick={() => navigate(`/courses/${slug}/${next.id}${q}`)}
                  >
                    <PlayCircle size={18} /> Continue: {next.title}
                  </button>
                ) : (
                  <span className="cs-badge-done">Course complete ✓</span>
                )}
              </header>

              {(data.terms || []).map((t) => {
                const open = !!openTerms[t.term];
                const finished = t.chapters_total > 0 && t.chapters_passed >= t.chapters_total;
                return (
                  <section key={t.term} className="cs-term">
                    <button
                      type="button"
                      className="cs-term-head"
                      aria-expanded={open}
                      onClick={() => setOpenTerms((o) => ({ ...o, [t.term]: !open }))}
                    >
                      <span className="cs-term-title">{t.term} Term</span>
                      <span className="cs-muted-sm">
                        {finished ? "Finished ✓ · " : ""}
                        {t.chapters_passed}/{t.chapters_total} chapters
                      </span>
                      <ChevronDown size={20} className={`cs-chev ${open ? "open" : ""}`} aria-hidden="true" />
                    </button>
                    {open && (
                      <div className="cs-topics">
                        {(t.topics || []).map((topic) => (
                          <article key={topic.id} className="cs-card cs-topic">
                            <div className="cs-topic-head">
                              <div>
                                <h3 className="cs-topic-title">{topic.title}</h3>
                                {topic.summary && <p className="cs-muted">{topic.summary}</p>}
                              </div>
                              <div className="cs-topic-meta">
                                {topic.access === "free" && <span className="cs-badge-free">Free</span>}
                                {topic.access === "locked" && <span className="cs-badge-lock"><Lock size={12} aria-hidden="true" /> Premium</span>}
                                {topic.complete && <span className="cs-badge-done">Topic complete</span>}
                                <span className="cs-muted-sm">{topic.chapters_passed}/{topic.chapters_total} chapters</span>
                              </div>
                            </div>
                            <ol className="cs-chapters">
                              {(topic.chapters || []).map((c) => {
                                const premium = topic.access === "locked";
                                const locked = c.status === "locked";
                                const inner = (
                                  <>
                                    <span className="cs-ch-num">{c.number}</span>
                                    <span className="cs-ch-title">{c.title}</span>
                                    {c.status === "passed" && c.best_score != null && (
                                      <span className="cs-ch-score">{c.best_score}%</span>
                                    )}
                                    <span className={`cs-status cs-status-${premium ? "locked" : c.status}`}>
                                      <StatusIcon status={premium ? "locked" : c.status} />
                                    </span>
                                  </>
                                );
                                return (
                                  <li key={c.id}>
                                    {premium ? (
                                      <Link
                                        to={`/subscribe?from=${encodeURIComponent(`/courses/${slug}/${c.id}${q}`)}`}
                                        className="cs-chapter locked premium"
                                        title="Subscribe to unlock this topic"
                                      >
                                        {inner}
                                      </Link>
                                    ) : locked ? (
                                      <div
                                        className="cs-chapter locked"
                                        title="Pass the previous chapter's quiz to unlock"
                                        aria-disabled="true"
                                      >
                                        {inner}
                                      </div>
                                    ) : (
                                      <Link to={`/courses/${slug}/${c.id}${q}`} className={`cs-chapter ${c.status}`}>
                                        {inner}
                                      </Link>
                                    )}
                                  </li>
                                );
                              })}
                            </ol>
                          </article>
                        ))}
                      </div>
                    )}
                  </section>
                );
              })}
            </>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default CourseSubject;
